import { Point, Wall, FloorPlanElement, WindowElement } from '../types';
import { distanceToLine } from './floorPlanUtils';
import { SNAP_THRESHOLD, WALL_THICKNESS } from '../constants/floorPlanConstants';

const DEFAULT_WINDOW_WIDTH = 60; // Default width for ESTATE mode
const DEFAULT_ROOF_WINDOW_WIDTH = 60; // Default roof window width for ESTATE mode
const DEFAULT_ROOF_WINDOW_HEIGHT = 80; // Default roof window height for ESTATE mode

// Smaller sizes for Retrofit/Energy mode (50% reduction)
const SMALL_WINDOW_WIDTH = 60;
const SMALL_ROOF_WINDOW_WIDTH = 60;
const SMALL_ROOF_WINDOW_HEIGHT = 80;

function getClosestPointOnSegment(point: Point, start: Point, end: Point): Point {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    if (dx === 0 && dy === 0) return start;
    const t = ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy);
    const clampedT = Math.max(0, Math.min(1, t));
    return { x: start.x + clampedT * dx, y: start.y + clampedT * dy };
}

// Helper to detect if a point is near a solid box window drag handle
export function getSolidBoxWindowDragHandle(point: Point, windowEl: WindowElement, wall: Wall): 'width-left' | 'width-right' | 'depth' | null {
    const { position, width = 120, depth = 40 } = windowEl;
    const handleSize = 12;
    const hitboxSize = 16; // Slightly larger for easier clicking
    
    // Calculate wall direction
    const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
    const wallLength = Math.hypot(wallVector.x, wallVector.y);
    if (wallLength === 0) return null;
    
    const wallDirection = { x: wallVector.x / wallLength, y: wallVector.y / wallLength };
    const perpendicular1 = { x: -wallDirection.y, y: wallDirection.x };
    const perpendicular2 = { x: wallDirection.y, y: -wallDirection.x };
    
    // Use stored side if available
    let perpendicular = perpendicular1;
    const storedSide = (windowEl as any).perpendicularSide;
    if (storedSide === 2) {
        perpendicular = perpendicular2;
    }
    
    const halfWidth = width / 2;
    const wallHalfThickness = WALL_THICKNESS / 2;
    
    // Calculate handle positions
    const wallInnerLeft = {
        x: position.x - wallDirection.x * halfWidth - perpendicular.x * wallHalfThickness,
        y: position.y - wallDirection.y * halfWidth - perpendicular.y * wallHalfThickness
    };
    const wallInnerRight = {
        x: position.x + wallDirection.x * halfWidth - perpendicular.x * wallHalfThickness,
        y: position.y + wallDirection.y * halfWidth - perpendicular.y * wallHalfThickness
    };
    
    const wallOuterLeft = {
        x: position.x - wallDirection.x * halfWidth + perpendicular.x * wallHalfThickness,
        y: position.y - wallDirection.y * halfWidth + perpendicular.y * wallHalfThickness
    };
    const wallOuterRight = {
        x: position.x + wallDirection.x * halfWidth + perpendicular.x * wallHalfThickness,
        y: position.y + wallDirection.y * halfWidth + perpendicular.y * wallHalfThickness
    };
    
    const boxLeftOut = {
        x: wallOuterLeft.x + perpendicular.x * depth,
        y: wallOuterLeft.y + perpendicular.y * depth
    };
    const boxRightOut = {
        x: wallOuterRight.x + perpendicular.x * depth,
        y: wallOuterRight.y + perpendicular.y * depth
    };
    
    const depthHandlePoint = {
        x: (boxLeftOut.x + boxRightOut.x) / 2,
        y: (boxLeftOut.y + boxRightOut.y) / 2
    };
    
    // Check left width handle
    if (Math.hypot(point.x - wallInnerLeft.x, point.y - wallInnerLeft.y) < hitboxSize) {
        return 'width-left';
    }
    
    // Check right width handle
    if (Math.hypot(point.x - wallInnerRight.x, point.y - wallInnerRight.y) < hitboxSize) {
        return 'width-right';
    }
    
    // Check depth handle
    if (Math.hypot(point.x - depthHandlePoint.x, point.y - depthHandlePoint.y) < hitboxSize) {
        return 'depth';
    }
    
    return null;
}

export function findWallForWindow(point: Point, walls: (Wall & { tempId: string, stableId: string })[]): (Wall & { tempId: string, stableId: string }) | null {
    let clickedWall: (Wall & { tempId: string, stableId: string }) | null = null;
    let minDistance = Infinity;

    for (const wall of walls) {
        const distance = distanceToLine(point, wall.start, wall.end);
        const closestPoint = getClosestPointOnSegment(point, wall.start, wall.end);
        const isOnSegment = Math.hypot(closestPoint.x - point.x, closestPoint.y - point.y) < SNAP_THRESHOLD * 2;

        if (distance < SNAP_THRESHOLD && isOnSegment) {
            if (distance < minDistance) {
                minDistance = distance;
                clickedWall = wall;
            }
        }
    }
    return clickedWall;
}

export function createWindowElement(
    point: Point,
    wall: Wall & { tempId:string, stableId: string },
    currentFloor: number,
    useSmallSize: boolean = false
): WindowElement | null {
    const windowWidth = useSmallSize ? SMALL_WINDOW_WIDTH : DEFAULT_WINDOW_WIDTH;
    console.log('🪟 createWindowElement:', { useSmallSize, windowWidth, SMALL_WINDOW_WIDTH, DEFAULT_WINDOW_WIDTH });
    const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
    if (wallLength < windowWidth) return null;

    const closestPoint = getClosestPointOnSegment(point, wall.start, wall.end);
    const distFromStart = Math.hypot(closestPoint.x - wall.start.x, closestPoint.y - wall.start.y);

    let positionOnWall = distFromStart / wallLength;

    const halfWindowWidthOnWall = (windowWidth / 2) / wallLength;
    positionOnWall = Math.max(halfWindowWidthOnWall, Math.min(positionOnWall, 1 - halfWindowWidthOnWall));

    const windowCenter = {
        x: wall.start.x + (wall.end.x - wall.start.x) * positionOnWall,
        y: wall.start.y + (wall.end.y - wall.start.y) * positionOnWall,
    };

    const newWindow: WindowElement = {
        id: `window-${Math.random().toString(36).substr(2, 9)}`,
        type: 'window',
        wallId: wall.stableId,
        positionOnWall,
        width: 0,
        height: 0,
        trickleVents: 0,
        floor: currentFloor,
        position: windowCenter,
        orientation: 'Unknown' // Will be calculated later
    };
    
    return newWindow;
}

// Create roof window as draggable canvas element
export function createRoofWindowElement(
    point: Point,
    currentFloor: number,
    useSmallSize: boolean = false
): WindowElement {
    const roofWidth = useSmallSize ? SMALL_ROOF_WINDOW_WIDTH : DEFAULT_ROOF_WINDOW_WIDTH;
    const roofHeight = useSmallSize ? SMALL_ROOF_WINDOW_HEIGHT : DEFAULT_ROOF_WINDOW_HEIGHT;
    console.log('🏠 createRoofWindowElement:', { useSmallSize, roofWidth, roofHeight, SMALL_ROOF_WINDOW_WIDTH, SMALL_ROOF_WINDOW_HEIGHT, DEFAULT_ROOF_WINDOW_WIDTH, DEFAULT_ROOF_WINDOW_HEIGHT });
    const newWindow: WindowElement = {
        id: `roof-window-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'window',
        wallId: null, // Roof windows are not attached to walls
        positionOnWall: 0, // Not applicable for roof windows
        width: roofWidth,
        height: roofHeight,
        trickleVents: 0,
        floor: currentFloor,
        position: point,
        orientation: 'Unknown',
        windowInstallationType: 'roof'
    };
    
    return newWindow;
}

// Create solid box window that extends outside the wall
export function createSolidBoxWindowElement(
    point: Point,
    wall: Wall & { tempId: string, stableId: string },
    currentFloor: number,
    useSmallSize: boolean = false
): WindowElement | null {
    const windowWidth = useSmallSize ? SMALL_WINDOW_WIDTH : DEFAULT_WINDOW_WIDTH;
    console.log('📦 createSolidBoxWindowElement:', { useSmallSize, windowWidth, SMALL_WINDOW_WIDTH, DEFAULT_WINDOW_WIDTH });
    const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
    if (wallLength < windowWidth) return null;

    const closestPoint = getClosestPointOnSegment(point, wall.start, wall.end);
    const distFromStart = Math.hypot(closestPoint.x - wall.start.x, closestPoint.y - wall.start.y);

    let positionOnWall = distFromStart / wallLength;

    const halfWindowWidthOnWall = (windowWidth / 2) / wallLength;
    positionOnWall = Math.max(halfWindowWidthOnWall, Math.min(positionOnWall, 1 - halfWindowWidthOnWall));

    const windowCenter = {
        x: wall.start.x + (wall.end.x - wall.start.x) * positionOnWall,
        y: wall.start.y + (wall.end.y - wall.start.y) * positionOnWall,
    };

    const newWindow: WindowElement = {
        id: `solid-box-window-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'window',
        wallId: wall.stableId,
        positionOnWall,
        width: 80, // Default wider for solid box windows
        height: 60, // Default height
        depth: 40, // Extension depth outside the wall
        trickleVents: 0,
        floor: currentFloor,
        position: windowCenter,
        orientation: 'Unknown',
        windowInstallationType: 'solid_box'
    };
    
    return newWindow;
}

// Draw roof window as boxed element (similar to loft hatch)
export function drawRoofWindow(ctx: CanvasRenderingContext2D, windowEl: WindowElement, scale: number, isSelected?: boolean) {
    const { position } = windowEl;
    
    ctx.save();
    
    // Always use default visual size for roof windows (ignore saved dimensions for visual consistency)
    const drawWidth = DEFAULT_ROOF_WINDOW_WIDTH;
    const drawHeight = DEFAULT_ROOF_WINDOW_HEIGHT;
    const x = position.x - drawWidth / 2;
    const y = position.y - drawHeight / 2;
    
    // Selection highlighting (behind window)
    if (isSelected) {
        ctx.fillStyle = 'rgba(59, 130, 246, 0.15)'; // Light blue background
        ctx.strokeStyle = '#4A5568'; // Dark gray outline  
        ctx.lineWidth = 2 / scale;
        const padding = 4;
        ctx.fillRect(x - padding, y - padding, drawWidth + padding * 2, drawHeight + padding * 2);
        ctx.strokeRect(x - padding, y - padding, drawWidth + padding * 2, drawHeight + padding * 2);
    }
    
    // Main window rectangle
    ctx.fillStyle = '#e6f7ff'; // Light blue background for roof window
    ctx.strokeStyle = '#1890ff'; // Blue border
    ctx.lineWidth = 2 / scale;
    
    // Main window rectangle
    ctx.fillRect(x, y, drawWidth, drawHeight);
    ctx.strokeRect(x, y, drawWidth, drawHeight);
    
    // Add cross pattern to indicate window glass
    ctx.strokeStyle = '#1890ff';
    ctx.lineWidth = 1 / scale;
    ctx.beginPath();
    // Vertical line
    ctx.moveTo(position.x, y);
    ctx.lineTo(position.x, y + drawHeight);
    // Horizontal line  
    ctx.moveTo(x, position.y);
    ctx.lineTo(x + drawWidth, position.y);
    ctx.stroke();
    
    // Add text label
    ctx.fillStyle = '#000000';
    ctx.font = `${Math.max(8, 10 / scale)}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('RW', position.x, position.y);
    
    ctx.restore();
}

// Draw roof window preview
export function drawRoofWindowPreview(ctx: CanvasRenderingContext2D, windowEl: WindowElement | null, scale: number) {
    if (!windowEl) return;
    
    ctx.save();
    
    // Preview with dashed lines
    ctx.setLineDash([5, 5]);
    ctx.strokeStyle = '#1890ff';
    ctx.lineWidth = 2 / scale;
    ctx.globalAlpha = 0.7;
    
    const { position } = windowEl;
    // Always use default visual size for roof window previews (ignore saved dimensions)
    const drawWidth = DEFAULT_ROOF_WINDOW_WIDTH;
    const drawHeight = DEFAULT_ROOF_WINDOW_HEIGHT;
    const x = position.x - drawWidth / 2;
    const y = position.y - drawHeight / 2;
    
    // Preview rectangle
    ctx.strokeRect(x, y, drawWidth, drawHeight);
    
    // Preview text
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#1890ff';
    ctx.font = `${Math.max(8, 10 / scale)}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('RW', position.x, position.y);
    
    ctx.restore();
}

// Draw box window that extends outside the wall
// Helper function to draw a mini wall window
function drawMiniWindow(
    ctx: CanvasRenderingContext2D,
    center: Point,
    width: number,
    height: number,
    perpDirection: Point,
    parallelDirection: Point,
    frameThickness: number,
    isSelected: boolean
) {
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    
    // Calculate four corners
    const topLeft = {
        x: center.x - perpDirection.x * halfWidth - parallelDirection.x * halfHeight,
        y: center.y - perpDirection.y * halfWidth - parallelDirection.y * halfHeight
    };
    const topRight = {
        x: center.x + perpDirection.x * halfWidth - parallelDirection.x * halfHeight,
        y: center.y + perpDirection.y * halfWidth - parallelDirection.y * halfHeight
    };
    const bottomRight = {
        x: center.x + perpDirection.x * halfWidth + parallelDirection.x * halfHeight,
        y: center.y + perpDirection.y * halfWidth + parallelDirection.y * halfHeight
    };
    const bottomLeft = {
        x: center.x - perpDirection.x * halfWidth + parallelDirection.x * halfHeight,
        y: center.y - perpDirection.y * halfWidth + parallelDirection.y * halfHeight
    };
    
    // Draw window background
    ctx.beginPath();
    ctx.moveTo(topLeft.x, topLeft.y);
    ctx.lineTo(topRight.x, topRight.y);
    ctx.lineTo(bottomRight.x, bottomRight.y);
    ctx.lineTo(bottomLeft.x, bottomLeft.y);
    ctx.closePath();
    
    ctx.fillStyle = 'rgba(135, 206, 250, 0.3)'; // Light blue for glass
    ctx.fill();
    
    // Draw window frame
    ctx.strokeStyle = isSelected ? '#1e40af' : '#333333';
    ctx.lineWidth = frameThickness;
    ctx.stroke();
    
    // Draw cross divider
    const centerTop = { x: (topLeft.x + topRight.x) / 2, y: (topLeft.y + topRight.y) / 2 };
    const centerBottom = { x: (bottomLeft.x + bottomRight.x) / 2, y: (bottomLeft.y + bottomRight.y) / 2 };
    const centerLeft = { x: (topLeft.x + bottomLeft.x) / 2, y: (topLeft.y + bottomLeft.y) / 2 };
    const centerRight = { x: (topRight.x + bottomRight.x) / 2, y: (topRight.y + bottomRight.y) / 2 };
    
    ctx.beginPath();
    ctx.moveTo(centerTop.x, centerTop.y);
    ctx.lineTo(centerBottom.x, centerBottom.y);
    ctx.moveTo(centerLeft.x, centerLeft.y);
    ctx.lineTo(centerRight.x, centerRight.y);
    ctx.strokeStyle = isSelected ? '#1e40af' : '#333333';
    ctx.lineWidth = 1;
    ctx.stroke();
}

export function drawSolidBoxWindow(ctx: CanvasRenderingContext2D, windowEl: WindowElement, wall: Wall, scale: number, isSelected?: boolean, allWalls?: Wall[]) {
    const { position, width = 120, height = 60, depth = 40 } = windowEl;
    
    ctx.save();
    
    // Calculate wall direction and perpendicular
    const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
    const wallLength = Math.hypot(wallVector.x, wallVector.y);
    if (wallLength === 0) {
        ctx.restore();
        return;
    }
    
    const wallDirection = { x: wallVector.x / wallLength, y: wallVector.y / wallLength };
    
    // Calculate both possible perpendicular directions
    const perpendicular1 = { x: -wallDirection.y, y: wallDirection.x };
    const perpendicular2 = { x: wallDirection.y, y: -wallDirection.x };
    
    // Determine which direction points away from the room interior with HYSTERESIS
    let perpendicular = perpendicular1;
    
    // Check if window has a stored side preference
    const storedSide = (windowEl as any).perpendicularSide;
    
    if (allWalls && allWalls.length > 2) {
        // Create test points in both directions
        const testDistance = depth * 0.5;
        const testPoint1 = {
            x: position.x + perpendicular1.x * testDistance,
            y: position.y + perpendicular1.y * testDistance
        };
        const testPoint2 = {
            x: position.x + perpendicular2.x * testDistance,
            y: position.y + perpendicular2.y * testDistance
        };
        
        // Count how many walls are "behind" each test point (indicating interior)
        let wallsBehindPoint1 = 0;
        let wallsBehindPoint2 = 0;
        
        for (const testWall of allWalls) {
            if (testWall === wall) continue; // Skip the current wall
            
            const wallMid = {
                x: (testWall.start.x + testWall.end.x) / 2,
                y: (testWall.start.y + testWall.end.y) / 2
            };
            
            const dist1 = Math.hypot(testPoint1.x - wallMid.x, testPoint1.y - wallMid.y);
            const dist2 = Math.hypot(testPoint2.x - wallMid.x, testPoint2.y - wallMid.y);
            
            if (dist1 < 200) wallsBehindPoint1++;
            if (dist2 < 200) wallsBehindPoint2++;
        }
        
        // HYSTERESIS: Require significant difference (3+ walls) to switch sides
        const HYSTERESIS_THRESHOLD = 3;
        
        if (storedSide === 1) {
            // Already using perpendicular1, only switch if perpendicular2 is significantly better
            if (wallsBehindPoint1 - wallsBehindPoint2 >= HYSTERESIS_THRESHOLD) {
                perpendicular = perpendicular2;
                (windowEl as any).perpendicularSide = 2;
            } else {
                perpendicular = perpendicular1;
            }
        } else if (storedSide === 2) {
            // Already using perpendicular2, only switch if perpendicular1 is significantly better
            if (wallsBehindPoint2 - wallsBehindPoint1 >= HYSTERESIS_THRESHOLD) {
                perpendicular = perpendicular1;
                (windowEl as any).perpendicularSide = 1;
            } else {
                perpendicular = perpendicular2;
            }
        } else {
            // First time, choose side with fewer walls and store it
            if (wallsBehindPoint2 < wallsBehindPoint1) {
                perpendicular = perpendicular2;
                (windowEl as any).perpendicularSide = 2;
            } else {
                perpendicular = perpendicular1;
                (windowEl as any).perpendicularSide = 1;
            }
        }
    }
    
    // Calculate simple rectangular box corners
    const halfWidth = width / 2;
    
    // STEP 1: CLEAR THE WALL GAP (make it look like window is IN the wall)
    const gapHalfWidth = halfWidth;
    const gapLeft = {
        x: position.x - wallDirection.x * gapHalfWidth,
        y: position.y - wallDirection.y * gapHalfWidth
    };
    const gapRight = {
        x: position.x + wallDirection.x * gapHalfWidth,
        y: position.y + wallDirection.y * gapHalfWidth
    };
    
    // Clear wall area where window sits
    ctx.beginPath();
    ctx.moveTo(gapLeft.x, gapLeft.y);
    ctx.lineTo(gapRight.x, gapRight.y);
    ctx.strokeStyle = ctx.canvas.style.backgroundColor || '#ffffff';
    ctx.lineWidth = WALL_THICKNESS + 4;
    ctx.lineCap = 'butt';
    ctx.stroke();
    
    // STEP 2: DRAW WINDOW FRAME aligned with wall thickness
    const wallHalfThickness = WALL_THICKNESS / 2;
    
    // Inner edge (room side) - slightly inside the wall
    const wallInnerLeft = {
        x: position.x - wallDirection.x * halfWidth - perpendicular.x * wallHalfThickness,
        y: position.y - wallDirection.y * halfWidth - perpendicular.y * wallHalfThickness
    };
    const wallInnerRight = {
        x: position.x + wallDirection.x * halfWidth - perpendicular.x * wallHalfThickness,
        y: position.y + wallDirection.y * halfWidth - perpendicular.y * wallHalfThickness
    };
    
    // Outer edge (exterior side) - slightly outside the wall
    const wallOuterLeft = {
        x: position.x - wallDirection.x * halfWidth + perpendicular.x * wallHalfThickness,
        y: position.y - wallDirection.y * halfWidth + perpendicular.y * wallHalfThickness
    };
    const wallOuterRight = {
        x: position.x + wallDirection.x * halfWidth + perpendicular.x * wallHalfThickness,
        y: position.y + wallDirection.y * halfWidth + perpendicular.y * wallHalfThickness
    };
    
    // Points extending out from the wall (box projection)
    const boxLeftOut = {
        x: wallOuterLeft.x + perpendicular.x * depth,
        y: wallOuterLeft.y + perpendicular.y * depth
    };
    const boxRightOut = {
        x: wallOuterRight.x + perpendicular.x * depth,
        y: wallOuterRight.y + perpendicular.y * depth
    };
    
    // Draw the rectangular box window (from inner wall edge to projection)
    ctx.beginPath();
    ctx.moveTo(wallInnerLeft.x, wallInnerLeft.y);
    ctx.lineTo(wallInnerRight.x, wallInnerRight.y);
    ctx.lineTo(wallOuterRight.x, wallOuterRight.y);
    ctx.lineTo(boxRightOut.x, boxRightOut.y);
    ctx.lineTo(boxLeftOut.x, boxLeftOut.y);
    ctx.lineTo(wallOuterLeft.x, wallOuterLeft.y);
    ctx.closePath();
    
    // Fill with white/light color
    ctx.fillStyle = isSelected ? 'rgba(224, 242, 254, 0.95)' : 'rgba(255, 255, 255, 0.95)';
    ctx.fill();
    
    // Draw thick border
    ctx.strokeStyle = isSelected ? '#1e40af' : '#000000';
    ctx.lineWidth = 3;
    ctx.stroke();
    
    // Draw three mini wall windows instead of dividers
    const windowPadding = 8; // Padding from edges
    const miniWindowWidth = 20; // Width of each mini window
    const miniWindowHeight = 25; // Height of each mini window
    const frameThickness = 2;
    
    // LEFT WINDOW - positioned on left side wall
    const leftSideCenter = {
        x: (wallOuterLeft.x + boxLeftOut.x) / 2,
        y: (wallOuterLeft.y + boxLeftOut.y) / 2
    };
    
    // Calculate perpendicular for left window orientation
    const leftWindowPerp = { x: wallDirection.x, y: wallDirection.y };
    const leftWindowParallel = { x: perpendicular.x, y: perpendicular.y };
    
    drawMiniWindow(ctx, leftSideCenter, miniWindowWidth, miniWindowHeight, leftWindowPerp, leftWindowParallel, frameThickness, isSelected || false);
    
    // RIGHT WINDOW - positioned on right side wall
    const rightSideCenter = {
        x: (wallOuterRight.x + boxRightOut.x) / 2,
        y: (wallOuterRight.y + boxRightOut.y) / 2
    };
    
    const rightWindowPerp = { x: -wallDirection.x, y: -wallDirection.y };
    const rightWindowParallel = { x: perpendicular.x, y: perpendicular.y };
    
    drawMiniWindow(ctx, rightSideCenter, miniWindowWidth, miniWindowHeight, rightWindowPerp, rightWindowParallel, frameThickness, isSelected || false);
    
    // TOP WINDOW - positioned on front wall
    const topCenter = {
        x: (boxLeftOut.x + boxRightOut.x) / 2,
        y: (boxLeftOut.y + boxRightOut.y) / 2
    };
    
    const topWindowPerp = { x: wallDirection.x, y: wallDirection.y };
    const topWindowParallel = { x: perpendicular.x, y: perpendicular.y };
    
    drawMiniWindow(ctx, topCenter, miniWindowWidth, miniWindowHeight, topWindowPerp, topWindowParallel, frameThickness, isSelected || false);
    
    // Draw selection highlight and drag handles if selected
    if (isSelected) {
        // Draw highlight outline
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        
        const padding = 6;
        ctx.beginPath();
        ctx.moveTo(wallInnerLeft.x - wallDirection.x * padding, wallInnerLeft.y - wallDirection.y * padding);
        ctx.lineTo(wallInnerRight.x + wallDirection.x * padding, wallInnerRight.y + wallDirection.y * padding);
        ctx.lineTo(boxRightOut.x + perpendicular.x * padding, boxRightOut.y + perpendicular.y * padding);
        ctx.lineTo(boxLeftOut.x + perpendicular.x * padding, boxLeftOut.y + perpendicular.y * padding);
        ctx.closePath();
        ctx.stroke();
        
        ctx.setLineDash([]);
        
        // Draw drag handles for width adjustment (left and right)
        const handleSize = 10;
        const handleOutlineSize = 12;
        
        // Left width handle
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(wallInnerLeft.x - handleOutlineSize/2, wallInnerLeft.y - handleOutlineSize/2, handleOutlineSize, handleOutlineSize);
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 2;
        ctx.strokeRect(wallInnerLeft.x - handleOutlineSize/2, wallInnerLeft.y - handleOutlineSize/2, handleOutlineSize, handleOutlineSize);
        
        // Right width handle
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(wallInnerRight.x - handleOutlineSize/2, wallInnerRight.y - handleOutlineSize/2, handleOutlineSize, handleOutlineSize);
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 2;
        ctx.strokeRect(wallInnerRight.x - handleOutlineSize/2, wallInnerRight.y - handleOutlineSize/2, handleOutlineSize, handleOutlineSize);
        
        // Depth handle (at front center)
        const depthHandlePoint = {
            x: (boxLeftOut.x + boxRightOut.x) / 2,
            y: (boxLeftOut.y + boxRightOut.y) / 2
        };
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(depthHandlePoint.x - handleOutlineSize/2, depthHandlePoint.y - handleOutlineSize/2, handleOutlineSize, handleOutlineSize);
        ctx.strokeStyle = '#22c55e';
        ctx.lineWidth = 2;
        ctx.strokeRect(depthHandlePoint.x - handleOutlineSize/2, depthHandlePoint.y - handleOutlineSize/2, handleOutlineSize, handleOutlineSize);
        
        // Draw measurement labels
        ctx.fillStyle = '#1e40af';
        ctx.font = 'bold 12px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        // Width label (along the wall)
        const widthLabelPos = {
            x: position.x - perpendicular.x * 20,
            y: position.y - perpendicular.y * 20
        };
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(widthLabelPos.x - 25, widthLabelPos.y - 10, 50, 20);
        ctx.fillStyle = '#1e40af';
        ctx.fillText(`${Math.round(width)}cm`, widthLabelPos.x, widthLabelPos.y);
        
        // Depth label (extending out)
        const depthLabelPos = {
            x: position.x + perpendicular.x * (depth / 2 + wallHalfThickness),
            y: position.y + perpendicular.y * (depth / 2 + wallHalfThickness)
        };
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(depthLabelPos.x - 25, depthLabelPos.y - 10, 50, 20);
        ctx.fillStyle = '#22c55e';
        ctx.fillText(`${Math.round(depth)}cm`, depthLabelPos.x, depthLabelPos.y);
    }
    
    ctx.restore();
}

function drawSingleWindow(ctx: CanvasRenderingContext2D, windowEl: WindowElement, wall: Wall, scale: number, viewOffset: Point, isSelected?: boolean) {
    const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
    const wallLength = Math.hypot(wallVector.x, wallVector.y);
    if (wallLength === 0) return;

    const wallDir = { x: wallVector.x / wallLength, y: wallVector.y / wallLength };
    const perpDir = { x: -wallDir.y, y: wallDir.x };

    // Use window position directly since canvas context is already transformed
    const windowCenter = {
        x: windowEl.position.x,
        y: windowEl.position.y,
    };
    const halfWidth = DEFAULT_WINDOW_WIDTH / 2; // Always use default width for consistent visual display

    const p1 = { x: windowCenter.x - halfWidth * wallDir.x, y: windowCenter.y - halfWidth * wallDir.y };
    const p2 = { x: windowCenter.x + halfWidth * wallDir.x, y: windowCenter.y + halfWidth * wallDir.y };

    // Define sillDepth early so it can be used in selection highlighting
    const sillDepth = WALL_THICKNESS / 1.8;

    // 🎯 SELECTION HIGHLIGHTING: Draw selection outline first (behind window)
    if (isSelected) {
        ctx.save();
        
        // Create a larger selection area around the window
        const selectionPadding = 8; // Padding around the window for selection highlight
        const selectionRadius = halfWidth + selectionPadding;
        
        const selection_p1 = { 
            x: windowCenter.x - selectionRadius * wallDir.x, 
            y: windowCenter.y - selectionRadius * wallDir.y 
        };
        const selection_p2 = { 
            x: windowCenter.x + selectionRadius * wallDir.x, 
            y: windowCenter.y + selectionRadius * wallDir.y 
        };
        
        const selection_sillDepth = sillDepth + selectionPadding;
        const selection_p1_outer = { 
            x: selection_p1.x + perpDir.x * selection_sillDepth, 
            y: selection_p1.y + perpDir.y * selection_sillDepth 
        };
        const selection_p2_outer = { 
            x: selection_p2.x + perpDir.x * selection_sillDepth, 
            y: selection_p2.y + perpDir.y * selection_sillDepth 
        };
        const selection_p1_inner = { 
            x: selection_p1.x - perpDir.x * selection_sillDepth, 
            y: selection_p1.y - perpDir.y * selection_sillDepth 
        };
        const selection_p2_inner = { 
            x: selection_p2.x - perpDir.x * selection_sillDepth, 
            y: selection_p2.y - perpDir.y * selection_sillDepth 
        };
        
        // Draw selection background
        ctx.fillStyle = 'rgba(59, 130, 246, 0.15)'; // Light blue background
        ctx.beginPath();
        ctx.moveTo(selection_p1_inner.x, selection_p1_inner.y);
        ctx.lineTo(selection_p2_inner.x, selection_p2_inner.y);
        ctx.lineTo(selection_p2_outer.x, selection_p2_outer.y);
        ctx.lineTo(selection_p1_outer.x, selection_p1_outer.y);
        ctx.closePath();
        ctx.fill();
        
        // Draw selection border
        ctx.strokeStyle = '#4A5568'; // Dark gray outline
        ctx.lineWidth = 1;
        ctx.closePath();
        ctx.stroke();
        
        ctx.restore();
    }

    // 1. Draw the gap in the wall
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.strokeStyle = ctx.canvas.style.backgroundColor || '#ffffff';
    ctx.lineWidth = WALL_THICKNESS + (2 / scale);
    ctx.lineCap = 'butt';
    ctx.stroke();
    ctx.restore();

    // 2. Draw the window symbol

    // NEW: Fill window area with a light color for visibility (brighter if selected)
    ctx.fillStyle = isSelected ? '#e0f2fe' : '#f0f4f8'; // Slightly brighter blue-tinted background when selected
    ctx.beginPath();
    const sill_p1_outer = { x: p1.x + perpDir.x * sillDepth, y: p1.y + perpDir.y * sillDepth };
    const sill_p2_outer = { x: p2.x + perpDir.x * sillDepth, y: p2.y + perpDir.y * sillDepth };
    const sill_p1_inner = { x: p1.x - perpDir.x * sillDepth, y: p1.y - perpDir.y * sillDepth };
    const sill_p2_inner = { x: p2.x - perpDir.x * sillDepth, y: p2.y - perpDir.y * sillDepth };
    
    ctx.moveTo(sill_p1_inner.x, sill_p1_inner.y);
    ctx.lineTo(sill_p2_inner.x, sill_p2_inner.y);
    ctx.lineTo(sill_p2_outer.x, sill_p2_outer.y);
    ctx.lineTo(sill_p1_outer.x, sill_p1_outer.y);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = isSelected ? '#1e40af' : '#2d3748'; // Darker blue when selected, otherwise dark gray
    ctx.setLineDash([]); // Reset to solid line for window drawing

    // Sill lines (thin)
    ctx.lineWidth = Math.max(0.5 / scale, 1.0 / scale);
    
    ctx.beginPath();
    ctx.moveTo(sill_p1_outer.x, sill_p1_outer.y);
    ctx.lineTo(sill_p2_outer.x, sill_p2_outer.y);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(sill_p1_inner.x, sill_p1_inner.y);
    ctx.lineTo(sill_p2_inner.x, sill_p2_inner.y);
    ctx.stroke();
    
    // Draw glass line with gap in middle for window ID
    ctx.lineWidth = isSelected ? Math.max(1.5 / scale, 3.0 / scale) : Math.max(0.75 / scale, 2.2 / scale);
    
    if (windowEl.displayId) {
        // Calculate gap size based on text width
        ctx.save();
        const fontSize = Math.max(14 / scale, 10); // Larger font size
        ctx.font = `bold ${fontSize}px Arial`; // Bold font for better visibility
        const textMetrics = ctx.measureText(windowEl.displayId);
        const textWidth = textMetrics.width;
        const gapSize = Math.max(textWidth + 12 / scale, 28 / scale); // Larger gap size
        
        // Calculate gap positions along the window line
        const totalLength = Math.hypot(p2.x - p1.x, p2.y - p1.y);
        const halfGap = gapSize / 2;
        const centerToGapStart = halfGap / totalLength;
        const centerToGapEnd = halfGap / totalLength;
        
        // Points for gap boundaries
        const gapStart = {
            x: windowCenter.x - wallDir.x * halfGap,
            y: windowCenter.y - wallDir.y * halfGap
        };
        const gapEnd = {
            x: windowCenter.x + wallDir.x * halfGap,
            y: windowCenter.y + wallDir.y * halfGap
        };
        
        // Extend the glass line segments beyond the window edges
        const extensionLength = 8 / scale; // Scale-aware extension
        const extendedP1 = {
            x: p1.x - wallDir.x * extensionLength,
            y: p1.y - wallDir.y * extensionLength
        };
        const extendedP2 = {
            x: p2.x + wallDir.x * extensionLength,
            y: p2.y + wallDir.y * extensionLength
        };
        
        // Draw first segment (extended p1 to gap start)
        ctx.beginPath();
        ctx.moveTo(extendedP1.x, extendedP1.y);
        ctx.lineTo(gapStart.x, gapStart.y);
        ctx.stroke();
        
        // Draw second segment (gap end to extended p2)
        ctx.beginPath();
        ctx.moveTo(gapEnd.x, gapEnd.y);
        ctx.lineTo(extendedP2.x, extendedP2.y);
        ctx.stroke();
        
        // Draw window ID in the gap - sleek with subtle box
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        // Use window's own styling
        const windowColor = isSelected ? '#1e40af' : '#4a5568';
        
        // Subtle box background
        const padding = 2 / scale;
        const boxWidth = textWidth + padding * 2;
        const boxHeight = fontSize * 0.8 + padding * 2;
        
        // Draw subtle rounded box
        const cornerRadius = 2 / scale;
        ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
        ctx.beginPath();
        ctx.roundRect(
            windowCenter.x - boxWidth/2,
            windowCenter.y - boxHeight/2,
            boxWidth,
            boxHeight,
            cornerRadius
        );
        ctx.fill();
        
        // Very subtle border
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
        ctx.lineWidth = 0.5 / scale;
        ctx.stroke();
        
        // Main text in window color
        ctx.fillStyle = windowColor;
        ctx.fillText(windowEl.displayId, windowCenter.x, windowCenter.y);
        
        ctx.restore();
    } else {
        // No ID - draw complete glass line
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.stroke();
    }
}

export function drawWindows(
    ctx: CanvasRenderingContext2D,
    elements: FloorPlanElement[],
    walls: (Wall & { tempId: string, stableId: string })[],
    currentFloor: number,
    scale: number,
    viewOffset: Point,
    selectedElement?: FloorPlanElement | null
) {
    const windows = elements.filter((e: any) => e.type === 'window' && e.floor === currentFloor) as unknown as WindowElement[];

    for (const windowEl of windows) {
        const wall = walls.find(w => w.stableId === windowEl.wallId);
        if (wall) {
            const isSelected = selectedElement?.type === 'window' && 
                              (selectedElement as any).id === windowEl.id;
            drawSingleWindow(ctx, windowEl, wall, scale, viewOffset, isSelected);
        }
    }
}

export function drawWindowPreview(
    ctx: CanvasRenderingContext2D,
    windowPreview: WindowElement | null,
    walls: (Wall & { tempId: string, stableId: string })[],
    scale: number,
    viewOffset: Point
) {
    if (!windowPreview) return;
    const wall = walls.find(w => w.stableId === windowPreview.wallId);
    if (wall) {
        ctx.save();
        ctx.globalAlpha = 0.7;
        drawSingleWindow(ctx, windowPreview, wall, scale, viewOffset, false);
        ctx.restore();
    }
} 